import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Sparkles, ArrowRight, Check, Loader2, Calendar, Image as ImageIcon,
    Film, PenLine, ExternalLink, Instagram, MapPin, Clock, AlertTriangle,
} from "lucide-react";
import axios from "axios";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { loginStudio } from "@/lib/studioAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const TIER_LABEL = { starter: "Starter", pro: "Standard", premium: "Premium" };

const TIER_NOTE = {
    starter: "Full text system + first 7 days of AI images free. Days 8-30: the cafe shoots with the daily shot scripts (each day card tells them exactly what to shoot).",
    pro: "Same full text system — but ALL 30 days of images are AI-generated. Zero shooting, no video needed.",
    premium: "Everything AI: images AND 5 ready-to-post reel videos rendered by our latest AI video engine. Completely hands-off.",
};

// ---------- Journey strip ----------
const JOURNEY = [
    { n: "01", t: "Buy a kit", d: "One Stripe checkout. RM 88 / RM 248 / RM 488. No calls, no quotes." },
    { n: "02", t: "5-minute brief", d: "The cafe fills one form — menu heroes, vibe, goals, brand colours." },
    { n: "03", t: "AI + Kaily craft", d: "The latest AI engines write every caption for this brand and render the visuals in its dark-and-orange style." },
    { n: "04", t: "Delivered in 48h", d: "A private kit page: calendar, captions, images, reel scripts, Publer CSV — ready to publish." },
];

function StatusBadge({ tier }) {
    if (tier.status === "delivered") {
        return (
            <span data-testid={`demo-status-${tier.tier}`} className="inline-flex items-center gap-1.5 rounded-full bg-cafe-olive text-cafe-cream px-3 py-1 text-[11px] uppercase tracking-[0.14em] font-bold">
                <Check size={12} /> Delivered
            </span>
        );
    }
    if (tier.ai_status === "failed") {
        return (
            <span data-testid={`demo-status-${tier.tier}`} className="inline-flex items-center gap-1.5 rounded-full bg-red-700 text-white px-3 py-1 text-[11px] uppercase tracking-[0.14em] font-bold">
                <AlertTriangle size={12} /> Failed
            </span>
        );
    }
    const label = tier.ai_status === "ready"
        ? `Rendering images ${tier.counts.images_ready}${tier.counts.images_total ? ` / ${tier.counts.images_total}` : ""}…`
        : "Writing content…";
    return (
        <span data-testid={`demo-status-${tier.tier}`} className="inline-flex items-center gap-1.5 rounded-full bg-cafe-terracotta/15 text-cafe-terracotta px-3 py-1 text-[11px] uppercase tracking-[0.14em] font-bold">
            <Loader2 size={12} className="animate-spin" /> {label}
        </span>
    );
}

function CountPill({ icon: Icon, label }) {
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-cafe-sand bg-white/70 px-3 py-1.5 text-[12px] text-cafe-espresso">
            <Icon size={13} className="text-cafe-terracotta" /> {label}
        </span>
    );
}

function CalendarStrip({ days }) {
    if (!days?.length) return null;
    return (
        <div className="mt-6">
            <p className="text-[11px] uppercase tracking-[0.18em] text-cafe-mocha font-bold mb-3">30-day calendar · first week</p>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                {days.map((d) => (
                    <div key={d.day} className="min-w-[240px] max-w-[240px] rounded-xl border border-cafe-sand bg-white p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-cafe-terracotta">Day {d.day}</span>
                            <span className="text-[10px] uppercase tracking-[0.1em] text-cafe-mocha bg-cafe-latte rounded-full px-2 py-0.5">{d.format}</span>
                        </div>
                        <p className="text-[13px] font-bold text-cafe-espresso leading-snug">{d.theme}</p>
                        <p className="text-[12px] text-cafe-mocha mt-1.5 italic leading-snug">"{d.hook}"</p>
                        <p className="text-[11px] text-cafe-mocha/70 mt-2 flex items-center gap-1"><Clock size={11} /> {d.best_time}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ImageGrid({ images, tier }) {
    if (!images?.length) return null;
    return (
        <div className="mt-6">
            <p className="text-[11px] uppercase tracking-[0.18em] text-cafe-mocha font-bold mb-3">AI images · rendered in the demo brand's style</p>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {images.slice(0, 10).map((img, i) => (
                    <div key={`${img.day}-${img.slide}-${i}`} className="relative aspect-square overflow-hidden rounded-lg border border-cafe-sand bg-cafe-latte">
                        <img src={img.url} alt={img.theme || `Day ${img.day}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" data-testid={`demo-img-${tier}-${i}`} />
                        <span className="absolute bottom-1 left-1 rounded bg-black/60 text-white text-[9px] px-1.5 py-0.5">D{img.day}{img.slide > 1 ? `·${img.slide}` : ""}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function VideoStrip({ videos, tier }) {
    if (!videos?.length) return null;
    return (
        <div className="mt-6">
            <p className="text-[11px] uppercase tracking-[0.18em] text-cafe-mocha font-bold mb-3">AI reel videos · ready to post</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {videos.map((v, i) => (
                    <div key={i} className="rounded-xl overflow-hidden border border-cafe-espresso/20 bg-black">
                        <video src={v.url} controls playsInline preload="metadata" className="w-full aspect-[9/16] object-cover" data-testid={`demo-video-${tier}-${i}`} />
                        <p className="text-[10px] text-cafe-cream/80 px-2 py-1.5 bg-cafe-espresso truncate">{v.title}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ReelCard({ reel }) {
    if (!reel) return null;
    return (
        <div className="mt-6 rounded-xl border border-cafe-espresso/15 bg-cafe-espresso text-cafe-cream p-5">
            <p className="text-[11px] uppercase tracking-[0.18em] text-cafe-terracotta font-bold mb-2 flex items-center gap-1.5"><Film size={13} /> Reel script sample</p>
            <p className="font-bold text-[15px]">{reel.title}</p>
            <p className="text-[13px] italic text-cafe-cream/80 mt-1">"{reel.hook}"</p>
            <div className="mt-3 space-y-1.5">
                {(reel.beats || []).map((b, i) => (
                    <p key={i} className="text-[12px] text-cafe-cream/70 leading-snug">▸ {b}</p>
                ))}
            </div>
        </div>
    );
}

function TierBlock({ tier, index }) {
    const p = tier.preview || {};
    return (
        <motion.section
            {...fadeUp}
            data-testid={`demo-tier-${tier.tier}`}
            className={`rounded-3xl border p-6 sm:p-10 ${index === 1 ? "border-cafe-olive/40 bg-[#F0EEE2]" : "border-cafe-sand bg-white/60"}`}
        >
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <h2 className="font-serif text-3xl sm:text-4xl text-cafe-espresso">{TIER_LABEL[tier.tier]}</h2>
                        <span className="font-serif text-xl text-cafe-terracotta">RM {tier.price_rm}</span>
                        <StatusBadge tier={tier} />
                    </div>
                    <p className="text-[13px] text-cafe-mocha mt-2">{tier.promise}</p>
                    <p className="text-[12.5px] text-cafe-espresso/80 mt-2 max-w-2xl leading-relaxed bg-cafe-terracotta/10 border border-cafe-terracotta/20 rounded-lg px-3 py-2">{TIER_NOTE[tier.tier]}</p>
                </div>
                {tier.kit_url && (
                    <a
                        href={tier.kit_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`demo-open-kit-${tier.tier}`}
                        className="inline-flex items-center gap-2 rounded-full bg-cafe-espresso text-cafe-cream px-6 py-3 text-[13px] uppercase tracking-[0.14em] font-bold hover:bg-cafe-olive transition-colors self-start"
                    >
                        Open the real delivery page <ExternalLink size={14} />
                    </a>
                )}
            </div>

            {/* counts */}
            <div className="flex flex-wrap gap-2 mt-5">
                <CountPill icon={PenLine} label={`${tier.counts.captions} captions`} />
                <CountPill icon={Sparkles} label={`${tier.counts.hooks} hooks`} />
                {tier.counts.reels > 0 && <CountPill icon={Film} label={`${tier.counts.reels} reel script${tier.counts.reels > 1 ? "s" : ""}`} />}
                {tier.counts.videos_ready > 0 && <CountPill icon={Film} label={`${tier.counts.videos_ready} AI reel videos`} />}
                {tier.counts.calendar_days > 0 && <CountPill icon={Calendar} label={`${tier.counts.calendar_days}-day calendar`} />}
                <CountPill icon={ImageIcon} label={`${tier.counts.images_ready} AI images${tier.status !== "delivered" && tier.counts.images_total ? ` of ${tier.counts.images_total}` : ""}`} />
            </div>

            {tier.ai_error && (
                <p className="mt-4 text-[13px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{tier.ai_error}</p>
            )}

            {/* hooks */}
            {p.hooks?.length > 0 && (
                <div className="mt-6">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-cafe-mocha font-bold mb-3">Hooks · scroll-stoppers written for this brand</p>
                    <div className="flex flex-wrap gap-2">
                        {p.hooks.map((h, i) => (
                            <span key={i} className="rounded-full border border-cafe-terracotta/30 bg-cafe-terracotta/5 text-cafe-espresso px-4 py-2 text-[13px] italic">"{h}"</span>
                        ))}
                    </div>
                </div>
            )}

            {/* captions */}
            {p.captions?.length > 0 && (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {p.captions.map((c, i) => (
                        <div key={i} className="rounded-xl border border-cafe-sand bg-white p-4">
                            <p className="text-[11px] uppercase tracking-[0.14em] text-cafe-terracotta font-bold mb-2">Caption {i + 1}</p>
                            <p className="text-[12.5px] text-cafe-espresso leading-relaxed whitespace-pre-line line-clamp-[10]">{c}</p>
                        </div>
                    ))}
                </div>
            )}

            <CalendarStrip days={p.calendar} />
            <ImageGrid images={p.images} tier={tier.tier} />
            <VideoStrip videos={p.videos} tier={tier.tier} />
            <ReelCard reel={p.reel} />
        </motion.section>
    );
}

// ---------- Month 2 continuity showcase ----------
function Month2Section({ month2, month1 }) {
    if (!month2) return null;
    const p = month2.preview || {};
    const generating = month2.status !== "delivered" && month2.ai_status !== "failed";
    const m1hooks = (month1?.preview?.hooks || []).slice(0, 3);
    return (
        <motion.section {...fadeUp} data-testid="demo-month2" className="rounded-3xl border border-cafe-olive/40 bg-cafe-espresso text-cafe-cream p-6 sm:p-10">
            <span className="inline-flex items-center gap-2 rounded-full bg-cafe-terracotta/20 text-cafe-terracotta px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold">
                <Sparkles size={13} /> Continuity subscribers · every month is fresh
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl mt-4">Month 2 — a completely new drop.</h2>
            <p className="text-cafe-cream/70 text-[14px] mt-3 max-w-2xl leading-relaxed">
                Subscribers don't get recycled content. Each month the system rotates the strategy — Month 1 pushed
                sales &amp; reach around {(month1?.preview ? "Chicken Fricassee" : "the hero menu")}; Month 2 shifts to brand story &amp; regulars,
                spotlighting {month2.hero_menu?.join(" + ") || "new hero dishes"}. New captions, new images, new angles.
            </p>
            {generating ? (
                <p className="mt-6 inline-flex items-center gap-2 text-cafe-terracotta text-[13px] font-bold uppercase tracking-[0.14em]">
                    <Loader2 size={14} className="animate-spin" /> Generating month 2 drop…
                </p>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-7">
                        <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                            <p className="text-[11px] uppercase tracking-[0.18em] text-cafe-terracotta font-bold mb-3">Month 1 hooks · sales &amp; reach</p>
                            {m1hooks.map((h, i) => (
                                <p key={i} className="text-[13px] italic text-cafe-cream/80 mb-2">"{h}"</p>
                            ))}
                        </div>
                        <div className="rounded-2xl bg-white/5 border border-cafe-terracotta/40 p-5">
                            <p className="text-[11px] uppercase tracking-[0.18em] text-cafe-terracotta font-bold mb-3">Month 2 hooks · brand &amp; regulars — all new</p>
                            {(p.hooks || []).slice(0, 3).map((h, i) => (
                                <p key={i} className="text-[13px] italic text-cafe-cream mb-2">"{h}"</p>
                            ))}
                        </div>
                    </div>
                    {p.images?.length > 0 && (
                        <div className="mt-5 flex gap-2 overflow-x-auto">
                            {p.images.map((img, i) => (
                                <img key={i} src={img.url} alt={`Month 2 day ${img.day}`} loading="lazy" className="h-28 w-28 rounded-lg object-cover border border-white/10" />
                            ))}
                        </div>
                    )}
                    {month2.kit_url && (
                        <a href={month2.kit_url} target="_blank" rel="noreferrer" data-testid="demo-open-month2"
                           className="inline-flex items-center gap-2 mt-6 rounded-full bg-cafe-terracotta text-white px-6 py-3 text-[12px] uppercase tracking-[0.14em] font-bold hover:bg-cafe-cream hover:text-cafe-espresso transition-colors">
                            Open the month 2 delivery <ExternalLink size={14} />
                        </a>
                    )}
                </>
            )}
        </motion.section>
    );
}

// ---------- Studio-only seed panel ----------
function SeedPanel({ onSeeded }) {
    const [pwd, setPwd] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const seed = async () => {
        setBusy(true); setErr("");
        try {
            await loginStudio(pwd);
            await axios.post(`${API}/demo/bayou-cafe/seed`, null);
            onSeeded();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Failed to start generation");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="max-w-md mx-auto rounded-2xl border border-cafe-sand bg-white p-8 text-center" data-testid="demo-seed-panel">
            <Sparkles className="mx-auto text-cafe-terracotta" size={28} />
            <h3 className="font-serif text-2xl text-cafe-espresso mt-3">Generate this case demo</h3>
            <p className="text-[13px] text-cafe-mocha mt-2">Studio only — runs the real AI pipeline for all 3 kits (≈30–60 min).</p>
            <input
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="Studio password"
                data-testid="demo-seed-password"
                className="mt-4 w-full rounded-full border border-cafe-sand px-5 py-3 text-sm focus:outline-none focus:border-cafe-olive"
            />
            {err && <p className="text-[12px] text-red-600 mt-2">{err}</p>}
            <button
                onClick={seed}
                disabled={busy || !pwd}
                data-testid="demo-seed-btn"
                className="mt-4 w-full rounded-full bg-cafe-espresso text-cafe-cream py-3 text-[13px] uppercase tracking-[0.16em] font-bold hover:bg-cafe-olive transition-colors disabled:opacity-50"
            >
                {busy ? "Starting…" : "Generate 3 demo kits"}
            </button>
        </div>
    );
}

export default function DemoCase() {
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const timerRef = useRef(null);

    const fetchDemo = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/demo/bayou-cafe`);
            setData(r.data);
        } catch { /* keep old data */ }
        setLoaded(true);
    }, []);

    useEffect(() => {
        fetchDemo();
    }, [fetchDemo]);

    // Poll while anything is still generating
    useEffect(() => {
        const tierGenerating = data?.seeded && data.tiers.some((t) => t.status !== "delivered" && t.ai_status !== "failed");
        const m2Generating = data?.month2 && data.month2.status !== "delivered" && data.month2.ai_status !== "failed";
        const generating = tierGenerating || m2Generating;
        if (generating && !timerRef.current) {
            timerRef.current = setInterval(fetchDemo, 8000);
        }
        if (!generating && timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
    }, [data, fetchDemo]);

    const cafe = data?.cafe || {};

    return (
        <div className="min-h-screen bg-cafe-cream text-cafe-espresso">
            <SiteNav lang="en" />
            {/* Hero */}
            <header className="pt-32 sm:pt-40 pb-14 px-6 max-w-6xl mx-auto">
                <motion.div {...fadeUp}>
                    <span className="inline-flex items-center gap-2 rounded-full border border-cafe-terracotta/40 bg-cafe-terracotta/10 text-cafe-terracotta px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold">
                        <Sparkles size={13} /> Demo simulation · real system output
                    </span>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
                        What <span className="italic text-cafe-terracotta">{cafe.name || "Kaily F&B Studio"}</span> would<br className="hidden sm:block" /> actually deliver.
                    </h1>
                    <p className="text-base text-cafe-mocha mt-5 max-w-2xl leading-relaxed">
                        We ran all three Growth Kits through our real pipeline — powered by the latest AI engines —
                        for a fictional demo cafe brand: its own menu, brand colours and Instagram voice. Nothing staged.
                        This is exactly what your cafe would receive.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-6 text-[12px] text-cafe-mocha">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-cafe-sand px-3 py-1.5"><Instagram size={13} className="text-cafe-terracotta" /> @{cafe.ig_handle}</span>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-cafe-sand px-3 py-1.5"><MapPin size={13} className="text-cafe-terracotta" /> {cafe.location}</span>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-cafe-sand px-3 py-1.5">{cafe.type}</span>
                    </div>
                </motion.div>
            </header>

            {/* Journey */}
            <section className="px-6 max-w-6xl mx-auto pb-16">
                <motion.div {...fadeUp} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {JOURNEY.map((s) => (
                        <div key={s.n} className="rounded-2xl border border-cafe-sand bg-white/70 p-6">
                            <span className="font-serif text-3xl text-cafe-terracotta/50">{s.n}</span>
                            <p className="font-bold text-[15px] mt-2">{s.t}</p>
                            <p className="text-[13px] text-cafe-mocha mt-1.5 leading-relaxed">{s.d}</p>
                        </div>
                    ))}
                </motion.div>
            </section>

            {/* Tier blocks / seed panel */}
            <main className="px-6 max-w-6xl mx-auto pb-24 space-y-10">
                {!loaded && (
                    <div className="text-center py-20 text-cafe-mocha"><Loader2 className="animate-spin mx-auto" size={28} /></div>
                )}
                {loaded && data && !data.seeded && <SeedPanel onSeeded={fetchDemo} />}
                {loaded && data?.seeded && data.tiers.map((t, i) => <TierBlock key={t.tier} tier={t} index={i} />)}
                {loaded && data?.seeded && <Month2Section month2={data.month2} month1={data.tiers.find((t) => t.tier === "pro")} />}
            </main>

            {/* CTA */}
            <section className="px-6 pb-28">
                <motion.div {...fadeUp} className="max-w-4xl mx-auto rounded-3xl bg-cafe-espresso text-cafe-cream p-10 sm:p-14 text-center">
                    <h2 className="font-serif text-3xl sm:text-4xl">Want this for your cafe?</h2>
                    <p className="text-cafe-cream/70 mt-3 text-[15px]">Same pipeline, your menu, your brand voice. Delivered in 48 hours.</p>
                    <Link
                        to="/ig/content/AI"
                        data-testid="demo-cta-offer"
                        className="inline-flex items-center gap-2 mt-8 rounded-full bg-cafe-terracotta text-white px-8 py-4 text-[13px] uppercase tracking-[0.16em] font-bold hover:bg-cafe-cream hover:text-cafe-espresso transition-colors"
                    >
                        See kits &amp; pricing <ArrowRight size={15} />
                    </Link>
                </motion.div>
            </section>
            <SiteFooter lang="en" />
        </div>
    );
}
